
export interface CatalogueLayer {
  configName: string;
  layerName: string;
  isBillable: boolean;
}

export interface CatalogueProduct {
  productKey: string;
  productName: string;
  productDescription: string;
  productCategory: string;
  isPremium: boolean;
  credits: number;
  singleUnit: boolean;
  layers: CatalogueLayer[];
  // Nested sub-product: rendered inside the parent's expander. Selecting it
  // implies selecting the parent; deselecting the parent removes it.
  parentProductKey?: string;
  // Dependency (not nested): shown at the top level but disabled until the
  // required product is selected; deselecting the required product clears it.
  requiresProductKey?: string;
}


export const PREMIUM_PRODUCT_KEYS: string[] = [
  "buildings",
  "buildings_height",
  "buildings_roof",
  "buildings_solar",
  "cadastre",
  "planning_insights",
  "property",
  "solar",
  "solar_address",
  "solar_building",
  "address_pool",
];


export interface LayerGroup {
  label: string;
  credits: number;
  configNames: string[];
}

// Aspatial layers that ride along with a billable layer, keyed by that layer's
// config_name. Sourced from the Data Tailor data dictionaries (foreign-key
// columns) and the published Geoscape docs.
export const LAYER_GROUP_COMPANIONS: Record<string, string[]> = {
  // Electoral: division attribute + authority tables join on the electoral PID.
  comm_electoral_polygon: ["comm_electoral"],
  state_electoral_polygon: ["state_electoral", "state_electoral_class_aut"],
  // SEIFA is a socio-economic score table joined to SA1 (SA1_2021_PID) with no
  // geometry of its own, so it rides with SA1 rather than billing separately.
  sa1_2021: ["seifa_2021"],
};

// Group labels that intentionally differ from the layer's catalogue name (the
// picker shows a shorter form). Everything else uses the layer name verbatim.
export const LAYER_GROUP_LABELS: Record<string, string> = {
  comm_electoral_polygon: "Commonwealth Electoral",
  state_electoral_polygon: "State Electoral",
};

// Category display order for the Choose Data step.
export const CATEGORY_ORDER: string[] = [
  "Address",
  "Administrative Boundaries",
  "Built Environment",
  "Land Parcels",
  "Statistical Boundaries",
  "Transport",
];

// Raw shape returned by GET /clip/products.
export interface ClipProductResponse {
  product_key: string;
  product_name: string;
  product_description: string;
  product_category: string;
  credits: number;
  is_premium: boolean;
  parent_product_key: string | null;
  requires_product_key: string | null;
  single_unit: boolean;
  layers: { config_name: string; layer_name: string; is_billable: boolean }[];
}

export const mapClipProduct = (raw: ClipProductResponse): CatalogueProduct => ({
  productKey: raw.product_key,
  productName: raw.product_name,
  productDescription: raw.product_description,
  productCategory: raw.product_category,
  isPremium: raw.is_premium,
  credits: raw.credits,
  singleUnit: raw.single_unit,
  layers: raw.layers.map((l) => ({
    configName: l.config_name,
    layerName: l.layer_name,
    isBillable: l.is_billable,
  })),
  parentProductKey: raw.parent_product_key ?? undefined,
  requiresProductKey: raw.requires_product_key ?? undefined,
});
